import type { CSSProperties, ReactNode } from "react";

export interface DialogProps {
  open?: boolean;
  title?: string;
  children?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  onClose?: () => void;
  width?: number;
  style?: CSSProperties;
}

/** Centered modal dialog with dimmed backdrop. Render conditionally on `open`. */
export function Dialog({
  open = true,
  title,
  children,
  primaryAction,
  secondaryAction,
  onClose,
  width = 360,
  style,
}: DialogProps) {
  if (!open) return null;
  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "var(--material-dimmer)",
        fontFamily: "var(--font-sans)",
      }}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose?.();
      }}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: purely stops the backdrop click from bubbling, not an interactive control. */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: "100%",
          background: "var(--background-elevated-normal)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-overlay)",
          padding: 24,
          boxSizing: "border-box",
          ...style,
        }}
      >
        {title && (
          <div
            style={{
              fontSize: "var(--font-size-heading-2)",
              lineHeight: "var(--line-height-heading-2)",
              fontWeight: 700,
              color: "var(--label-normal)",
              marginBottom: 8,
            }}
          >
            {title}
          </div>
        )}
        {children && (
          <div
            style={{
              fontSize: "var(--font-size-body-2)",
              lineHeight: "var(--line-height-body-2-reading)",
              color: "var(--label-alternative)",
            }}
          >
            {children}
          </div>
        )}
        {(primaryAction || secondaryAction) && (
          <div style={{ display: "flex", gap: 8, marginTop: 24 }}>
            {secondaryAction}
            {primaryAction}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dialog;
